import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search } from 'lucide-react';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  competition: any;
  category?: string | null;
  onSuccess: () => void;
}

export function CreateMatchModal({ isOpen, onClose, competition, category, onSuccess }: Props) {
  const [player1Id, setPlayer1Id] = useState<string>('');
  const [player2Id, setPlayer2Id] = useState<string>('');
  const [round, setRound] = useState<string>('');
  const [stage, setStage] = useState<string>('');
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const participants = competition?.participants || [];
  const filteredParticipants = participants.filter((p: any) => {
    const name = (p.user?.name || p.name || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return name.includes(search.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase());
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('tt_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const res = await fetch(`${API_URL}/api/competitions/${competition.id}/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          player1Id: player1Id || null,
          player2Id: player2Id || null,
          round: round ? parseInt(round) : null,
          stage: stage || null,
          category: category || null
        })
      });
      
      if (res.ok) {
        toast.success('Partida criada com sucesso!');
        setPlayer1Id('');
        setPlayer2Id('');
        setRound('');
        setStage('');
        onSuccess();
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Erro ao criar partida');
      }
    } catch (err) {
      toast.error('Erro de conexão ao criar partida');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }} 
          onClick={onClose}
        >
          <motion.div
            style={{
              background: "#161129",
              width: "100%", maxWidth: "500px", borderRadius: "8px", overflow: "hidden",
              display: "flex", flexDirection: "column", boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
              maxHeight: "90vh",
            }}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div style={{ background: "#6136B3", padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ color: "white", fontSize: "20px", fontWeight: 700, margin: 0 }}>Criar Nova Partida</h2>
              <button onClick={onClose} style={{ background: "none", border: "none", color: "white", cursor: "pointer", display: "flex" }}>
                <X size={24} />
              </button>
            </div>
            
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{ position: 'absolute', left: '16px', top: '14px', color: 'rgba(255,255,255,0.4)' }} />
                <input 
                  type="text" 
                  placeholder="Buscar participante..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px 12px 48px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              
              <form id="create-match-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                 <div>
                   <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Jogador 1</label>
                   <select 
                     value={player1Id} 
                     onChange={(e) => setPlayer1Id(e.target.value)}
                     style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                   >
                     <option value="" style={{color: 'black'}}>-- A Definir --</option>
                     {filteredParticipants.map((p: any) => (
                       <option key={p.id} value={p.userId || p.id} style={{color: 'black'}}>
                         {p.user?.name || p.name}
                       </option>
                     ))}
                   </select>
                 </div>
                 
                 <div>
                   <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Jogador 2</label>
                   <select 
                     value={player2Id} 
                     onChange={(e) => setPlayer2Id(e.target.value)}
                     style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                   >
                     <option value="" style={{color: 'black'}}>-- A Definir --</option>
                     {filteredParticipants.map((p: any) => (
                       <option key={p.id} value={p.userId || p.id} style={{color: 'black'}}>
                         {p.user?.name || p.name}
                       </option>
                     ))}
                   </select>
                 </div>
    
                 <div style={{ display: 'flex', gap: '16px' }}>
                   <div style={{ flex: 1 }}>
                     <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Rodada (Opcional)</label>
                     <input 
                       type="number"
                       placeholder="Ex: 1"
                       value={round}
                       onChange={(e) => setRound(e.target.value)}
                       style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                     />
                   </div>
                   <div style={{ flex: 1 }}>
                     <label style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Fase (Opcional)</label>
                     <input 
                       type="text"
                       placeholder="Ex: Fase de Grupos"
                       value={stage}
                       onChange={(e) => setStage(e.target.value)}
                       style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none', boxSizing: 'border-box' }}
                     />
                   </div>
                 </div>
              </form>
            </div>
            
            <div style={{ padding: '20px 24px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
              <button 
                type="button" 
                onClick={onClose} 
                style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                form="create-match-form"
                disabled={isSubmitting} 
                style={{ padding: '10px 20px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', transition: 'transform 0.2s' }}
              >
                {isSubmitting ? 'Criando...' : 'Criar Partida'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
